import React, { ReactNode, useState } from 'react'
import { useKey } from 'react-use'
import Modal from 'components/Modal'
import { Button, ButtonVariant } from '@sketch/components'
import { keyWithoutModifier } from '@sketch/utils'
import { ModalInjectedProps } from 'containers/ModalManager'

interface ButtonProps {
  text?: string
  variant?: ButtonVariant
  loading?: boolean
  disabled?: boolean
}

export interface ConfirmationDialogProps extends ModalInjectedProps {
  title: ReactNode
  cancelButton?: ButtonProps
  onHide?: () => void
  confirmButton: ButtonProps
  onConfirm: () => void
  onCancelAction?: () => void
  children: React.ReactNode
}

const ConfirmationDialog = ({
  title,
  hideModal,
  cancelButton: cancelButtonProp,
  onHide,
  confirmButton: confirmButtonProp,
  onConfirm,
  onCancelAction,
  children,
}: ConfirmationDialogProps) => {
  const [isTabPressed, setIsTabPressed] = useState(false)

  const cancelButton = { text: 'Cancel', disabled: false, ...cancelButtonProp }
  const confirmButton = {
    variant: 'negative' as ButtonVariant,
    ...confirmButtonProp,
  }

  const handleCancel = () => {
    onHide?.()
    hideModal()

    if (onCancelAction) {
      // This callback serves to execute an action
      // right after the modal is closed.
      // Needed to open chained modals in ProjectSharingModal.tsx
      onCancelAction()
      return
    }
  }

  const handleConfirmOnEnterPressed = () => {
    // In order to mantain "Cancel" and "Confirm" buttons accessible when
    // navigating with "tab", it's only possible to confirm when "Enter" is
    // pressed before using "tab" navigation.
    // The usage of "tab" will disable this functionality.
    if (!isTabPressed) onConfirm()
  }

  const handleTabPressed = () => {
    setIsTabPressed(true)
  }

  useKey(keyWithoutModifier('Enter'), handleConfirmOnEnterPressed, {
    event: 'keyup',
  })
  useKey(keyWithoutModifier('Tab'), handleTabPressed, { event: 'keyup' })

  return (
    <Modal title={title} onCancel={handleCancel}>
      <Modal.Body>{children}</Modal.Body>
      <Modal.Footer>
        <Button
          onClick={handleCancel}
          disabled={cancelButton.disabled}
          variant={cancelButton.variant}
        >
          {cancelButton.text}
        </Button>
        <Button
          data-testid="confirmation-button"
          variant={confirmButton.variant}
          loading={confirmButton.loading}
          disabled={confirmButton.disabled}
          onClick={onConfirm}
        >
          {confirmButton.text}
        </Button>
      </Modal.Footer>
    </Modal>
  )
}

export default ConfirmationDialog
